// Per-row action menu — RON-5469. Prototype region: `.js-row-action` + `#dd-row-actions`.
//
// The same actions the detail panel offers, reachable WITHOUT selecting the row first. At a busy
// desk, signing somebody out should not cost two clicks and a context switch.
//
// Only the actions that exist are rendered. The prototype's menu has seven entries; three of them
// have nothing behind them, and a menu item that only raises a toast is a promise the screen
// cannot keep:
//
//   row-print   badge printing is an integration item (§9)
//   row-edit    there is NO reception edit endpoint — the contract has nine routes, and none of
//               them edits a visit
//   row-block   `vcm.visitor.block` is a capability with no table behind it (§9)

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// The prototype's own icons, one per action. Ours had none at all, which left a column of bare
// labels where the prototype gives the eye a shape to aim at — on a menu that is opened mid-task,
// beside somebody waiting at a desk, the icon is what makes it scannable rather than readable.
//
// Each is the prototype's exact path, so they match the same icons used elsewhere on the screen.
const Icon = ({ children }) => (
  <svg
    className="reception-rowmenu__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const ICONS = {
  view: (
    <>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Not in the prototype's menu — it has no check-in entry, because its fixture never shows an
  // expected visitor there. An arrow INTO a door, mirroring the sign-out arrow leaving one.
  checkIn: (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </>
  ),
  checkOut: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  notify: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  badge: (
    <>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </>
  ),
  edit: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  ),
  block: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </>
  ),
  vip: (
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  ),
};

const ReceptionRowMenu = ({
  row,
  isBusy,
  onView,
  onCheckIn,
  onCheckOut,
  onNotifyHost,
  onToggleVip,
  // The three items with no endpoint. The menu does not decide how that is shown — the shell
  // owns the popup.
  onUnavailable,
}) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => event.key === "Escape" && setIsOpen(false);

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const run = (action) => () => {
    setIsOpen(false);
    action?.(row);
  };

  return (
    <div className="reception-rowmenu" ref={wrapRef}>
      <button
        type="button"
        className="reception-rowmenu__trigger"
        // The row behind this is itself clickable; without this the menu would open AND select.
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen((current) => !current);
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t("react_reception_row_actions").replace("%s", row.name)}
      >
        ⋯
      </button>

      {isOpen && (
        <div className="reception-rowmenu__menu" role="menu">
          <button type="button" role="menuitem" className="reception-rowmenu__item" onClick={run(onView)}>
            <Icon>{ICONS.view}</Icon>
            {t("react_reception_view_details")}
          </button>

          {/* No endpoint behind it. Shown and refusing out loud, for the same reason the detail
              panel's does: a receptionist cannot tell a missing feature from a missing control,
              and an absent row reads as the second. */}
          <button type="button" role="menuitem" className="reception-rowmenu__item" onClick={run(() => onUnavailable?.("react_reception_unavailable_badge"))}>
            <Icon>{ICONS.badge}</Icon>
            {t("react_reception_print_badge")}
          </button>

          <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onNotifyHost)}>
            <Icon>{ICONS.notify}</Icon>
            {t("react_reception_notify_host")}
          </button>

          <button type="button" role="menuitem" className="reception-rowmenu__item" onClick={run(() => onUnavailable?.("react_reception_unavailable_edit"))}>
            <Icon>{ICONS.edit}</Icon>
            {t("react_reception_edit_details")}
          </button>

          <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onToggleVip)}>
            <Icon>{ICONS.vip}</Icon>
            {t(row.isVip ? "react_reception_remove_vip" : "react_reception_mark_vip")}
          </button>

          {/* Driven by the SERVER's flags, exactly as the detail panel is — not inferred from
              `status`, because the rules include things a row cannot show you (§4). The prototype
              has only "Sign out" here, because its fixture visitor is already in; this slot is
              whichever of the two the server says is available. */}
          {row.canCheckIn && (
            <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onCheckIn)}>
              <Icon>{ICONS.checkIn}</Icon>
              {t("react_reception_check_in")}
            </button>
          )}

          {row.canCheckOut && (
            <button type="button" role="menuitem" className="reception-rowmenu__item" disabled={isBusy} onClick={run(onCheckOut)}>
              <Icon>{ICONS.checkOut}</Icon>
              {t("react_reception_check_out")}
            </button>
          )}

          {/* The prototype separates Block visitor from the rest and colours it as a destructive
              action — it is the only item here that does something TO a person rather than for
              them. `vcm.visitor.block` is a capability with no table behind it (§9). */}
          <span className="reception-rowmenu__divider" role="presentation" />

          <button type="button" role="menuitem" className="reception-rowmenu__item reception-rowmenu__item--danger" onClick={run(() => onUnavailable?.("react_reception_unavailable_block"))}>
            <Icon>{ICONS.block}</Icon>
            {t("react_reception_block_visitor")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReceptionRowMenu;
